import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5001/api';
const API_KEY = process.env.REACT_APP_API_KEY;

const api = axios.create({
  baseURL: API_URL
});

api.apiKey = API_KEY;

// Add token to all requests if available
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('adminToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  } else if (API_KEY) {
    config.headers['X-Api-Key'] = API_KEY;
  }
  return config;
});

// Handle 401 errors globally
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminRole');
      localStorage.removeItem('isFirstLogin');
      window.location.href = '/admin/login';
    }
    return Promise.reject(error);
  }
);

const getAuthHeaders = () => {
  const token = localStorage.getItem('adminToken');
  const headers = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  } else if (API_KEY) {
    headers['X-Api-Key'] = API_KEY;
  }
  return headers;
};

// --- API VERSION PREFIX ---
const V1 = '/v1';
// --- END API VERSION PREFIX ---


// ==================== PUBLIC ROUTES ====================

/**
 * Get colleges with advanced filtering and sorting
 */
export const getColleges = (params) => api.get(`${V1}/colleges`, { params });

/**
 * Search colleges by course name
 */
export const searchCollegesByCourse = (courseName, additionalParams = {}) => {
  return api.get(`${V1}/colleges`, {
    params: {
      courseName,
      ...additionalParams
    }
  });
};

/**
 * Search colleges by location
 */
export const searchCollegesByLocation = (city, state, additionalParams = {}) => {
  const params = { ...additionalParams };
  if (city) params.city = city;
  if (state) params.state = state;
  return api.get(`${V1}/colleges`, { params });
};

/**
 * Get colleges sorted by fees
 */
export const getCollegesSortedByFees = (order = 'asc', additionalParams = {}) => {
  return api.get(`${V1}/colleges`, {
    params: {
      sortBy: 'fees',
      sortOrder: order,
      ...additionalParams
    }
  });
};

/**
 * Get colleges sorted by rating
 */
export const getCollegesSortedByRating = (order = 'desc', additionalParams = {}) => {
  return api.get(`${V1}/colleges`, {
    params: {
      sortBy: 'rating',
      sortOrder: order,
      ...additionalParams
    }
  });
};

/**
 * Advanced search with multiple criteria
 */
export const advancedCollegeSearch = ({
  courseName,
  city,
  state,
  minFee,
  maxFee,
  sortBy = 'name',
  sortOrder = 'asc',
  page = 1,
  limit = 10
}) => {
  const params = {
    page,
    limit,
    sortBy,
    sortOrder
  };

  if (courseName) params.courseName = courseName;
  if (city) params.city = city;
  if (state) params.state = state;
  if (minFee !== undefined && maxFee !== undefined) {
    params.tuitionFee = `${minFee},${maxFee}`;
  }

  return api.get(`${V1}/colleges`, { params });
};

export const getCollegeById = (id) => api.get(`${V1}/colleges/${encodeURIComponent(id)}`);
export const searchColleges = (query) => api.get(`${V1}/colleges/search`, { params: { q: query } });
export const getCollegeStats = () => api.get(`${V1}/colleges/stats/summary`);

// ==================== DOWNLOAD FUNCTIONS ====================

/**
 * Get proper image/file URL (AWS S3 or backend)
 */
export const getFileUrl = (filePath) => {
  if (!filePath) return null;
  
  // If it's already a full URL (AWS S3 or other CDN), return as is
  if (filePath.startsWith('http://') || filePath.startsWith('https://')) {
    return filePath;
  }
  
  // Otherwise, construct backend URL
  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5001/api';
  const host = backendUrl.replace('/api', '');
  return `${host}/uploads/${filePath}`;
};

/**
 * Download colleges data as Excel
 * @param {Object} params - Download parameters
 * @param {number} params.limit - Number of colleges to download (0 = all)
 * @param {string} params.sortBy - Sort field (name, rating, fees, etc.)
 * @param {string} params.sortOrder - Sort order (asc/desc)
 */
export const downloadCollegesExcel = async (params = {}) => {
  try {
    const response = await api.get(`${V1}/colleges/download/excel`, {
      params,
      responseType: 'blob'
    });

    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    
    // Extract filename from headers or use default
    const contentDisposition = response.headers['content-disposition'];
    let filename = 'colleges.xlsx';
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
      if (filenameMatch) filename = filenameMatch[1];
    }
    
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);

    return { success: true };
  } catch (error) {
    console.error('Download Excel failed:', error);
    throw error;
  }
};

/**
 * Download colleges data as JSON
 * @param {Object} params - Download parameters
 */
export const downloadCollegesJSON = async (params = {}) => {
  try {
    const response = await api.get(`${V1}/colleges/download/json`, {
      params,
      responseType: 'blob'
    });

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    
    const contentDisposition = response.headers['content-disposition'];
    let filename = 'colleges.json';
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
      if (filenameMatch) filename = filenameMatch[1];
    }
    
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);

    return { success: true };
  } catch (error) {
    console.error('Download JSON failed:', error);
    throw error;
  }
};

// ==================== COLLEGE MANAGEMENT (Protected) ====================

export const createCollegeWithFiles = (collegeData, logoFile, bannerFile, brochureFile) => {
  const formData = new FormData();
  formData.append('data', JSON.stringify(collegeData));

  if (logoFile) {
    formData.append('collegeLogo', logoFile, logoFile.name);
  }
  if (bannerFile) {
    formData.append('bannerImage', bannerFile, bannerFile.name);
  }
  if (brochureFile) {
    formData.append('brochure', brochureFile, brochureFile.name);
  }

  return api.post(`${V1}/colleges`, formData, {
    headers: {
      ...getAuthHeaders(),
      'Content-Type': 'multipart/form-data',
    },
  });
};

export const updateCollegeWithFiles = (id, collegeData, logoFile, bannerFile, brochureFile) => {
  const formData = new FormData();
  formData.append('data', JSON.stringify(collegeData));

  if (logoFile) {
    formData.append('collegeLogo', logoFile, logoFile.name);
  }
  if (bannerFile) {
    formData.append('bannerImage', bannerFile, bannerFile.name);
  }
  if (brochureFile) {
    formData.append('brochure', brochureFile, brochureFile.name);
  }

  return api.put(`${V1}/colleges/${id}`, formData, {
    headers: {
      ...getAuthHeaders(),
      'Content-Type': 'multipart/form-data',
    },
  });
};

export const bulkUploadCollegesWithFiles = (csvFile, logoFiles = [], bannerFiles = [], brochureFiles = []) => {
  const formData = new FormData();
  formData.append('file', csvFile);

  logoFiles.forEach(file => {
    if (file) {
      formData.append('logos', file, file.name);
    }
  });

  bannerFiles.forEach(file => {
    if (file) {
      formData.append('banners', file, file.name);
    }
  });

  brochureFiles.forEach(file => {
    if (file) {
      formData.append('brochures', file, file.name);
    }
  });

  return api.post(`${V1}/colleges/bulk`, formData, {
    headers: {
      ...getAuthHeaders(),
      'Content-Type': 'multipart/form-data',
    },
  });
};

export const createCollege = (formData) => {
  return api.post(`${V1}/colleges`, formData, {
    headers: {
      ...getAuthHeaders(),
      'Content-Type': 'multipart/form-data'
    }
  });
};

export const updateCollege = (id, formData) => {
  return api.put(`${V1}/colleges/${id}`, formData, {
    headers: {
      ...getAuthHeaders(),
      'Content-Type': 'multipart/form-data'
    }
  });
};

export const deleteCollege = (id) => {
  return api.delete(`${V1}/colleges/${id}`, {
    headers: getAuthHeaders()
  });
};

export const bulkUploadColleges = (file) => {
  const formData = new FormData();
  formData.append('file', file);

  return api.post(`${V1}/colleges/bulk`, formData, {
    headers: {
      ...getAuthHeaders(),
      'Content-Type': 'multipart/form-data'
    }
  });
};

export const downloadCollegeTemplate = () => {
  return api.get(`${V1}/colleges/download-template`, {
    headers: getAuthHeaders(),
    responseType: 'blob'
  });
};

// ==================== FILTER DATA ENDPOINTS ====================
export const getAvailableStates = () => api.get(`${V1}/colleges/filters/states`);
export const getCitiesByState = (state) => api.get(`${V1}/colleges/filters/cities?state=${state}`);
export const getAvailableCourses = () => api.get(`${V1}/colleges/filters/courses`);

// ==================== OTHER ENDPOINTS ====================
export const adminSignup = (email) => api.post(`${V1}/admin/signup`, { email });
export const adminLogin = (email, password) => api.post(`${V1}/admin/login`, { email, password });
export const adminChangePassword = (oldPassword, newPassword) => {
  return api.post(`${V1}/auth/change-password`, { oldPassword, newPassword }, { 
    headers: getAuthHeaders() 
  });
};

export const getAdminProfile = () => api.get(`${V1}/admin/profile`, { headers: getAuthHeaders() });
export const updateAdminProfile = (data) => api.put(`${V1}/admin/profile`, data, { headers: getAuthHeaders() });

export const getNotifications = () => api.get(`${V1}/admin/notifications`, { headers: getAuthHeaders() });
export const markNotificationAsRead = (id) => api.put(`${V1}/admin/notifications/${id}/read`, {}, { headers: getAuthHeaders() });
export const deleteNotification = (id) => api.delete(`${V1}/admin/notifications/${id}`, { headers: getAuthHeaders() });

export const getAnalyticsData = () => api.get(`${V1}/admin/analytics`, { headers: getAuthHeaders() });
export const getTrafficData = (period = '7days') => api.get(`${V1}/admin/analytics/traffic?period=${period}`, { headers: getAuthHeaders() });
export const getConversionData = () => api.get(`${V1}/admin/analytics/conversions`, { headers: getAuthHeaders() });
export const getTopColleges = () => api.get(`${V1}/admin/analytics/top-colleges`, { headers: getAuthHeaders() });
export const getRecentActivity = () => api.get(`${V1}/admin/analytics/recent-activity`, { headers: getAuthHeaders() });

export const getSettings = () => api.get(`${V1}/admin/settings`, { headers: getAuthHeaders() });
export const updateSettings = (data) => api.put(`${V1}/admin/settings`, data, { headers: getAuthHeaders() });
export const updateEmailSettings = (data) => api.put(`${V1}/admin/settings/email`, data, { headers: getAuthHeaders() });
export const updateSEOSettings = (data) => api.put(`${V1}/admin/settings/seo`, data, { headers: getAuthHeaders() });
export const updateSecuritySettings = (data) => api.put(`${V1}/admin/settings/security`, data, { headers: getAuthHeaders() });

export const getAllAdmins = () => api.get(`${V1}/admin/all`, { headers: getAuthHeaders() });
export const createAdmin = (data) => api.post(`${V1}/admin/create`, data, { headers: getAuthHeaders() });
export const updateAdminRole = (id, role) => api.put(`${V1}/admin/${id}/role`, { role }, { headers: getAuthHeaders() });
export const deleteAdmin = (id) => api.delete(`${V1}/admin/${id}`, { headers: getAuthHeaders() });
export const getActivityLogs = () => api.get(`${V1}/admin/activity-logs`, { headers: getAuthHeaders() });

export const createAdminForm = (formData) => api.post(`${V1}/admin/forms`, formData, { headers: getAuthHeaders() });
export const getAdminForms = (params = {}) => api.get(`${V1}/admin/forms`, { params, headers: getAuthHeaders() });
export const createFormLink = (formId) => api.post(`${V1}/admin/forms/links?formId=${formId}`, {}, { headers: getAuthHeaders() });

export const getContactSubmissions = () => api.get(`${V1}/contact/submissions`, { headers: getAuthHeaders() });
export const deleteContactSubmission = (id) => api.delete(`${V1}/contact/submissions/${id}`, { headers: getAuthHeaders() });

export const getCoursesByCollege = (collegeId) => api.get(`${V1}/colleges/${collegeId}/courses`, { headers: getAuthHeaders() });
export const createCourse = (collegeId, data) => api.post(`${V1}/colleges/${collegeId}/courses`, data, { headers: getAuthHeaders() });
export const updateCourse = (collegeId, courseId, data) => api.put(`${V1}/colleges/${collegeId}/courses/${courseId}`, data, { headers: getAuthHeaders() });
export const deleteCourse = (collegeId, courseId) => api.delete(`${V1}/colleges/${collegeId}/courses/${courseId}`, { headers: getAuthHeaders() });

export const getReviewsByCollege = (collegeId) => api.get(`${V1}/colleges/${collegeId}/reviews`);
export const approveReview = (reviewId) => api.put(`${V1}/reviews/${reviewId}/approve`, {}, { headers: getAuthHeaders() });
export const deleteReview = (reviewId) => api.delete(`${V1}/reviews/${reviewId}`, { headers: getAuthHeaders() });

export const uploadImage = (formData) => {
  return api.post(`${V1}/upload/image`, formData, {
    headers: {
      ...getAuthHeaders(),
      'Content-Type': 'multipart/form-data',
    },
  });
};

export const validateImageFile = (file) => {
  if (!file) return { valid: false, error: 'No file provided' };

  const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
  const maxSize = 5 * 1024 * 1024;

  if (!validTypes.includes(file.type)) {
    return { valid: false, error: 'Invalid file type. Please upload JPG, PNG, GIF, WEBP, or SVG.' };
  }

  if (file.size > maxSize) {
    return { valid: false, error: 'File size exceeds 5MB limit.' };
  }

  return { valid: true, error: null };
};

export const validateBrochureFile = (file) => {
  if (!file) return { valid: false, error: 'No file provided' };

  const validTypes = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  ];
  const maxSize = 10 * 1024 * 1024;

  if (!validTypes.includes(file.type)) {
    return { valid: false, error: 'Invalid file type. Please upload PDF, DOC, DOCX, PPT, PPTX, XLS, or XLSX.' };
  }

  if (file.size > maxSize) {
    return { valid: false, error: 'File size exceeds 10MB limit.' };
  }

  return { valid: true, error: null };
};

export const validateCSVFile = (file) => {
  if (!file) return { valid: false, error: 'No file provided' };

  const validTypes = ['text/csv', 'application/vnd.ms-excel'];
  const maxSize = 50 * 1024 * 1024;

  if (!validTypes.includes(file.type) && !file.name.endsWith('.csv')) {
    return { valid: false, error: 'Invalid file type. Please upload a CSV file.' };
  }

  if (file.size > maxSize) {
    return { valid: false, error: 'CSV file size exceeds 50MB limit.' };
  }

  return { valid: true, error: null };
};

// ==================== BROCHURE / OTP ENDPOINTS ====================

// 1. Initial brochure request
export const requestBrochure = (data) => {
    return api.post(`${V1}/brochure/request`, data);
  };
  
  // 2. Verify OTP for brochure
  export const verifyBrochureOTP = (data) => {
    return api.post(`${V1}/brochure/verify-otp`, data);
  };
  
  // 3. Complete account after OTP verification
  export const completeBrochureAccount = (data) => {
    return api.post(`${V1}/brochure/complete-account`, data);
  };
  

export default api;